<template>
  <v-container id="Requisition-items">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>
    <v-row>
      <v-col
        cols="12"
        class="d-flex justify-end"
        v-if="requisitionStatus === 'pending' && canSelectAnyItem"
      >
        <v-btn
        v-if="hasPermission('requisition.transfer')"
          color="success"
          @click="transferSelected"
          :disabled="selectedItems.length === 0"
        >
          <v-icon left>mdi-transfer</v-icon>
          Transfer
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="requisitionStatus === 'pending' && canSelectAnyItem">
      <v-col cols="12" class="d-flex justify-end">
        <v-checkbox
          v-model="selectAll"
          label="Select All"
          @change="toggleSelectAll"
          :indeterminate="isIndeterminate"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="items"
      dense
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style=" border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:body="{ items }">
        <tr v-for="(item, index) in items" :key="index">
          <td>
            <v-autocomplete
              v-model="item.product_id"
              :items="products"
              item-text="name"
              item-value="id"
              dense
              outlined
              clearable
              :rules="[rules.required]"
              :loading="isFetchingProducts"
              :filter="() => true"
              @update:search-input="
                (searchTerm) => onSearchProducts(searchTerm)
              "
              placeholder="Product Name"
              :disabled="readonly"
            ></v-autocomplete>
          </td>
          <td class="wider-description">
            <v-text-field
              v-model="item.description"
              placeholder="Description"
              dense
              outlined
              clearable
              :readonly="readonly"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model.number="item.quantity"
              placeholder="Quantity"
              type="number"
              dense
              outlined
              :readonly="readonly"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model.number="item.transferred_quantity"
              placeholder="Transferred Quantity"
              type="number"
              dense
              outlined
              :readonly="readonly"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model.number="item.transfer_quantity"
              :placeholder="`Max: ${maxTransferable(item)}`"
              type="number"
              dense
              outlined
              :rules="[
                (v) =>
                  v <= maxTransferable(item) ||
                  `Cannot exceed ${maxTransferable(item)}`,
                (v) => v >= 0 || 'Value cannot be negative',
              ]"
              @keypress="restrictInput($event, item)"
            />
          </td>

          <td
            v-if="
              requisitionStatus === 'pending' &&
                item.quantity > item.transferred_quantity &&
                item.stock_masters_in_branch1 &&
                item.stock_masters_in_branch1.reduce(
                  (acc, stock) =>
                    acc + parseFloat(stock.stock_in_hand_quantity || 0),
                  0
                ) > 0
            "
          >
            <v-checkbox v-model="selectedItems" :value="item"></v-checkbox>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="closeSnackbar"
    />
    <Snackbars
      :text="'Stock transfer failed. Please try again.'"
      :snackbar="snackbarError"
      :color="'red'"
      @closeSnackbar="closeSnackbar"
    />
  </v-container>
</template>

<script>
import { RequisitionService } from "@/services/requisition-service.js";
import { StockTransferService } from "@/services/stockTransfer-service.js";
import { checkError } from "@/config/errors.js";
import Snackbars from "@/components/shared/Snackbars";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "ItemsTable",
  components: {
    Snackbars,
  },
  props: {
    items: { type: Array, required: true },
    selectedStores: {
      type: Array,
      default: () => [],
    },
    productsArray: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
      default: () => [
        { text: "Product", value: "product_id" },
        { text: "Description", value: "description" },
        { text: "Request Quantity", value: "request_quantity" },
        {
          text: "Quantity Can Transfer",
          value: "Quantity Can Transfer",
        },
        { text: "", value: "actions", sortable: false },
      ],
    },
    title: { type: String, default: "Items" },
    readonly: { type: Boolean, default: false },
    requisitionId: { type: [String, Number], default: null },
    requisitionStatus: { type: String },
    requisition: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      isFetchingProducts: false,
      ProductSearchTimeout: null,
      products: [...this.productsArray],
      rules: {
        required: (value) => !!value || "This field is required.",
      },
      selectedItems: [],
      selectAll: false,
      loading: false,
      errors: null,
      snackbar: false,
      snackbarError: false,
      successMsg: "Stock transfer successfully",
      branches: [],
      stores: [],
      userId: localStorage.getItem("userId"),
      inBranch: null,
      inStore: null,
    };
  },

  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    isIndeterminate() {
      // Only count selectable items for indeterminate state
      const selectableCount = this.items.filter(
        (item) => item.quantity > item.transferred_quantity
      ).length;
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < selectableCount
      );
    },
    canSelectAnyItem() {
      return this.items.some(
        (item) => item.quantity > item.transferred_quantity
      );
    },
  },
  watch: {
    selectedItems(newVal) {
      const selectableCount = this.items.filter(
        (item) => item.quantity > item.transferred_quantity
      ).length;
      if (newVal.length === selectableCount) {
        this.selectAll = true;
      } else if (newVal.length === 0) {
        this.selectAll = false;
      }
    },
    selectedStores: {
      handler: function(val) {
        console.log("Selected stores:", val);
      },
      immediate: true,
      deep: true,
    },
    items() {
      this.selectedItems = [];
      this.selectAll = false;
    },
  },

  mounted() {
    this.fetchBranches();
    this.setBranchesAndStore();
  },

  methods: {
    async onSearchProducts(searchTerm) {
      if (!searchTerm) {
        return;
      }

      try {
        clearTimeout(this.ProductSearchTimeout);
        this.isFetchingProducts = true;
        this.ProductSearchTimeout = setTimeout(async () => {
          const response = await RequisitionService.getProducts(searchTerm);
          const newProducts = response.data.data;
          this.products = [...new Set([...this.products, ...newProducts])];
        }, 3000);
      } catch (error) {
        console.error("Error fetching Products:", error);
      } finally {
        this.isFetchingProducts = false;
      }
    },

    restrictInput(event, item) {
      const char = String.fromCharCode(event.which);
      const newValue = `${item.transfer_quantity || ""}${char}`;
      const numericValue = parseInt(newValue, 10);

      if (isNaN(numericValue)) return;

      if (numericValue > this.maxTransferable(item)) {
        event.preventDefault();
      }
    },
    maxTransferable(item) {
      const sum = item.stock_masters_in_branch1
        ? item.stock_masters_in_branch1.reduce(
            (acc, stock) => acc + parseFloat(stock.stock_in_hand_quantity || 0),
            0
          )
        : 0;
      return Math.min(sum, item.quantity - item.transferred_quantity);
    },

    removeStore(store) {
      this.selectedStores = this.selectedStores.filter(
        (s) => s.id !== store.id
      );
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.items.filter(
          (item) => item.quantity > item.transferred_quantity
        );
      } else {
        this.selectedItems = [];
      }
    },

    async setBranchesAndStore() {
      this.loading = true;
      this.inBranch = this.requisition.branch_id;
      if (this.inBranch !== null || !this.inBranch !== undefined) {
        try {
          const storeResponse = await StockTransferService.getBranchMainStore(
            this.inBranch
          );
          this.inStore = storeResponse.data.data;
        } catch (error) {
          console.error("Error fetching data:", error);
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        }
      }
      this.loading = false;
    },

    toggleSelection(item) {
      const index = this.selectedItems.findIndex(
        (selectedItem) => selectedItem === item
      );
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
    },
    selectAllItems() {
      this.selectedItems = [...this.items];
    },
    unselectAllItems() {
      this.selectedItems = [];
    },

    async fetchBranches() {
      this.loading = true;
      try {
        const branchesResponse = await StockTransferService.getMainBranchAndMainStores();
        this.branches = branchesResponse.data;
        this.stores = this.branches.reduce(
          (acc, branch) => acc.concat(branch.stores || []),
          []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      }
      this.loading = false;
    },

    async transferSelected() {
      const transferItems = [];
      const outBranchSet = new Set();
      const outStoreSet = new Set();

      this.selectedItems.forEach((item) => {
        let remainingQty = item.quantity - item.transferred_quantity;
        for (const batch of item.stock_masters_in_branch1) {
          const batchQty = parseFloat(batch.stock_in_hand_quantity || 0);
          if (remainingQty <= 0) break;
          const transferQty = item.transfer_quantity
            ? Math.min(batchQty, item.transfer_quantity, remainingQty)
            : Math.min(batchQty, remainingQty);

          if (transferQty > 0) {
            transferItems.push({
              product_id: item.product_id,
              product_code: item.product.product_code,
              batch_id: batch.batch_id,
              store_id: batch.store_id,
              branch_id: batch.branch_id,
              quantity: transferQty,
            });
            outBranchSet.add(batch.branch_id);
            outStoreSet.add(batch.store_id);
            remainingQty -= transferQty;
          }
        }
      });

      const formData = {
        items: transferItems,
        requisitionId: this.requisitionId,
        inBranch: this.inBranch,
        inStore: this.inStore,
        userId: this.userId,
        status: "Pending",
        transfer: "Requisition",
      };

      await this.submitForm(formData);
    },

    async submitForm(formData) {
      this.loading = true;
      try {
        await StockTransferService.createTransfer(formData);
        this.snackbar = true;
        this.snackbarError = false;
        window.location.reload();
        this.$emit("transfer-success");
      } catch (error) {
        console.error(error);
        let data = error.response?.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
        this.snackbarError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}

.v-input--selection-controls {
  margin-top: 1px;
  padding-top: 1px;
}
</style>
